import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Checkbox,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { useVariableContext } from '@/contexts/VariableContext';
import { scientificToNumeric } from '@/utils/util-functions';
import { useActionFormContext } from './ActionFormContext';

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledTextField = styled(TextField)({
  '& input[type="number"]::-webkit-inner-spin-button, & input[type="number"]::-webkit-outer-spin-button':
    {
      WebkitAppearance: 'none',
      margin: 0,
    },
});

export const ActionToStateTable: React.FC = () => {
  const {
    newStateItems,
    mutuallyExclusive,
    errorMessage,
    errorItemIds,
    hasError,
    handleProbChange,
    handleProbBlur,
    handleProbTypeChange,
    handleRemainingChange,
    handleSelectChange,
    handleDeleteToStateItem,
  } = useActionFormContext();
  const { variableList } = useVariableContext();
  const hasRemainingItem = newStateItems?.some(item => item.remaining);

  const calculateProb = () => {
    const hasVarProb = newStateItems?.some(
      item => item.probType === 'variable',
    );

    if (hasVarProb) {
      return 'Calculated at runtime';
    } else {
      // Check if all prob values are valid numbers
      const convertedProbValues = newStateItems?.map(item =>
        typeof item.prob === 'string'
          ? scientificToNumeric(item.prob)
          : item.prob,
      );

      if (
        !convertedProbValues?.every(
          item => typeof item === 'number' && !Number.isNaN(item),
        )
      ) {
        return 'Unable to calculate';
      }
      const sumOfProbs
        = convertedProbValues
          .filter(item => item !== -1)
          .reduce(
            (acc, item) =>
              typeof item === 'number' && typeof acc === 'number'
                ? acc + item
                : 0,
            0,
          ) ?? 0;

      const remainingProb = 1 - sumOfProbs;
      if (remainingProb > 1) {
        return 'Invalid Probability';
      }
      const roundedProb = Math.round(remainingProb * 1e10) / 1e10;
      return Number.isInteger(roundedProb)
        ? roundedProb.toFixed(0)
        : roundedProb.toFixed(10).replace(/\.?0+$/, '');
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table aria-label="Action To State Table">
        <TableHead>
          <StyledTableRow>
            <StyledTableCell sx={{ fontWeight: 'bold', p: 2 }}>
              To State
            </StyledTableCell>
            <StyledTableCell sx={{ fontWeight: 'bold' }}>
              Fixed Value or Variable
            </StyledTableCell>
            <StyledTableCell sx={{ fontWeight: 'bold' }}>
              Probability
            </StyledTableCell>
            <StyledTableCell sx={{ fontWeight: 'bold' }}>
              Command
            </StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {newStateItems?.map((item, i) => (
            <StyledTableRow key={item.id}>
              <StyledTableCell component="th" scope="row">
                {item.toState}
              </StyledTableCell>
              <StyledTableCell>
                <FormControl>
                  <RadioGroup
                    aria-labelledby="prob type"
                    name="prob-type"
                    value={item.probType}
                    onChange={e => {
                      handleProbTypeChange(e, item);
                    }}
                  >
                    <FormControlLabel
                      value="fixed"
                      control={<Radio />}
                      label="Fixed"
                      sx={{ mr: 0, height: 25 }}
                    />
                    <FormControlLabel
                      value="variable"
                      control={<Radio />}
                      disabled={item.remaining}
                      label="Variable"
                      sx={{ mr: 0, height: 25 }}
                    />
                  </RadioGroup>
                </FormControl>
              </StyledTableCell>
              <StyledTableCell>
                {item.probType === 'fixed' ? (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {mutuallyExclusive
                        && ((hasRemainingItem && item.remaining)
                          || !hasRemainingItem) ? (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={item.prob === -1}
                                  disabled={hasRemainingItem && !item.remaining}
                                  onChange={e => {
                                    handleRemainingChange(e, item);
                                  }}
                                />
                              }
                              label="Remaining"
                            />
                          ) : (
                            <></>
                          )}
                      {item.remaining ? (
                        <Typography sx={{ ml: 2 }}>
                          {calculateProb()}
                        </Typography>
                      ) : (
                        <StyledTextField
                          label="Probability"
                          value={item.prob}
                          type="text" // Change type to text
                          disabled={item.prob === -1}
                          id="prob value"
                          size="small"
                          onChange={e => {
                            handleProbChange(e, item);
                          }}
                          onBlur={() => {
                            handleProbBlur(item);
                          }}
                          slotProps={{
                            htmlInput: {
                              style: {
                                WebkitTextFillColor:
                                  item.prob === -1 ? 'transparent' : 'currentColor',
                              },
                            },
                          }}
                          error={hasError && errorItemIds.has(item.id)}
                          helperText={
                            errorItemIds.has(item.id) && errorMessage
                              ? errorMessage
                              : ''
                          }
                        />
                      )}
                    </Box>
                  </>
                ) : (
                  <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <InputLabel id={`variable-${i.toString()}-label`}>
                      Select Variable
                    </InputLabel>
                    <Select
                      aria-labelledby={`variable-${i.toString()}-label`}
                      label="Select Variable"
                      value={item.varProb ?? ''}
                      onChange={e => {
                        handleSelectChange(e, item);
                      }}
                      displayEmpty
                    >
                      {variableList.value.map(variable => (
                        <MenuItem value={variable.name} key={variable.id}>
                          {variable.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </StyledTableCell>
              <StyledTableCell>
                <Tooltip title="Delete Row">
                  <DeleteIcon
                    sx={{ cursor: 'pointer', ml: 3 }}
                    onClick={() => {
                      handleDeleteToStateItem(item.id);
                    }}
                  />
                </Tooltip>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
